import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useUser } from "@clerk/clerk-react";
import { useQueryClient } from "@tanstack/react-query";
import * as api from "@/src/api/sdk.gen";
import type { B2bOrgCreditSummaryResponse } from "@/src/api/types.gen";
import type {
  OrgStats,
  MemberRow,
  InvitationItem,
  OrgDocument,
  WorkspaceItem,
  WorkspaceDetail,
} from "../types";

export interface OrgDashboardContextValue {
  slug: string;
  stats: OrgStats | null;
  loadingStats: boolean;
  loadStats: (force?: boolean) => Promise<void>;
  isOrgAdmin: boolean;
  activeWorkspaceSlug: string | null;
  setActiveWorkspaceSlug: (slug: string | null) => void;

  // Workspaces
  workspaces: WorkspaceItem[];
  loadingWorkspaces: boolean;
  loadWorkspaces: (force?: boolean) => Promise<void>;
  selectedWorkspace: WorkspaceItem | null;
  setSelectedWorkspace: (ws: WorkspaceItem | null) => void;
  detailedWorkspace: WorkspaceDetail | null;
  loadingWorkspaceDetail: boolean;
  loadWorkspaceDetail: (wsSlug: string, force?: boolean) => Promise<void>;

  // Members & Invites
  members: MemberRow[];
  loadingMembers: boolean;
  loadMembers: (force?: boolean) => Promise<void>;
  invitations: InvitationItem[];
  loadingInvites: boolean;
  loadInvites: (force?: boolean) => Promise<void>;

  // Documents
  documents: OrgDocument[];
  loadingDocs: boolean;
  loadDocuments: (force?: boolean) => Promise<void>;

  // AI Credits & Billing
  creditsSummary: B2bOrgCreditSummaryResponse | null;
  loadingCredits: boolean;
  loadCredits: (force?: boolean) => Promise<void>;

  // Global cache & UI reload
  refreshAll: (force?: boolean) => Promise<void>;

  // Computed metrics
  seatUtilization: {
    used: number;
    total: number;
    percentage: number;
    isNearLimit: boolean;
    isAtLimit: boolean;
  };
  pendingInvitesCount: number;
}

const OrgDashboardContext = createContext<OrgDashboardContextValue | null>(null);

export interface OrgDashboardProviderProps {
  slug: string;
  children: React.ReactNode;
}

export const OrgDashboardProvider: React.FC<OrgDashboardProviderProps> = ({ slug, children }) => {
  const { user } = useUser();
  const queryClient = useQueryClient();
  const [stats, setStats] = useState<OrgStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [activeWorkspaceSlug, setActiveWorkspaceSlug] = useState<string | null>(null);

  // Workspaces state
  const [workspaces, setWorkspaces] = useState<WorkspaceItem[]>([]);
  const [loadingWorkspaces, setLoadingWorkspaces] = useState(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState<WorkspaceItem | null>(null);
  const [detailedWorkspace, setDetailedWorkspace] = useState<WorkspaceDetail | null>(null);
  const [loadingWorkspaceDetail, setLoadingWorkspaceDetail] = useState(false);

  // Members & Invites
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [invitations, setInvitations] = useState<InvitationItem[]>([]);
  const [loadingInvites, setLoadingInvites] = useState(false);

  // Documents
  const [documents, setDocuments] = useState<OrgDocument[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(false);

  // AI Credits
  const [creditsSummary, setCreditsSummary] = useState<B2bOrgCreditSummaryResponse | null>(null);
  const [loadingCredits, setLoadingCredits] = useState(false);

  // Cache tracking refs and in-flight promise deduplication
  const statsLoadedRef = useRef(false);
  const inFlightStatsPromise = useRef<Promise<void> | null>(null);

  const creditsLoadedRef = useRef(false);
  const inFlightCreditsPromise = useRef<Promise<void> | null>(null);


  const workspacesLoadedRef = useRef(false);
  const inFlightWorkspacesPromise = useRef<Promise<void> | null>(null);

  const membersLoadedRef = useRef(false);
  const inFlightMembersPromise = useRef<Promise<void> | null>(null);

  const invitesLoadedRef = useRef(false);
  const inFlightInvitesPromise = useRef<Promise<void> | null>(null);

  const docsLoadedRef = useRef(false);
  const inFlightDocsPromise = useRef<Promise<void> | null>(null);

  const loadedWsSlugRef = useRef<string | null>(null);
  const inFlightWsDetailPromise = useRef<Record<string, Promise<void> | undefined>>({});

  // Reset tracking refs and states when slug changes
  useEffect(() => {
    statsLoadedRef.current = false;
    inFlightStatsPromise.current = null;
    workspacesLoadedRef.current = false;
    inFlightWorkspacesPromise.current = null;
    membersLoadedRef.current = false;
    inFlightMembersPromise.current = null;
    invitesLoadedRef.current = false;
    inFlightInvitesPromise.current = null;
    docsLoadedRef.current = false;
    inFlightDocsPromise.current = null;
    creditsLoadedRef.current = false;
    inFlightCreditsPromise.current = null;
    loadedWsSlugRef.current = null;
    inFlightWsDetailPromise.current = {};
    setStats(null);
    setWorkspaces([]);
    setSelectedWorkspace(null);
    setDetailedWorkspace(null);
    setMembers([]);
    setInvitations([]);
    setDocuments([]);
    setCreditsSummary(null);
  }, [slug]);

  const loadStats = useCallback(async (force?: boolean) => {
    if (!slug) return;
    if (!force) {
      if (statsLoadedRef.current) return;
      if (inFlightStatsPromise.current) return inFlightStatsPromise.current;
    }
    const promise = (async () => {
      try {
        setLoadingStats(true);
        const { data } = await api.getOrgDashboardStatsApiV1B2bSlugDashboardGet({
          path: { slug },
        });
        if (data) {
          setStats(data as unknown as OrgStats);
          statsLoadedRef.current = true;
        }
      } catch (err) {
        console.error("Failed to load org stats:", err);
      } finally {
        setLoadingStats(false);
        inFlightStatsPromise.current = null;
      }
    })();
    inFlightStatsPromise.current = promise;
    return promise;
  }, [slug]);

  const loadWorkspaces = useCallback(async (force?: boolean) => {
    if (!slug) return;
    if (!force) {
      if (workspacesLoadedRef.current) return;
      if (inFlightWorkspacesPromise.current) return inFlightWorkspacesPromise.current;
    }
    const promise = (async () => {
      try {
        setLoadingWorkspaces(true);
        const { data } = await api.listWorkspacesApiV1B2bSlugWorkspacesGet({
          path: { slug },
        });
        if (data) {
          const wsList = (data as any).workspaces || [];
          setWorkspaces(wsList);
          workspacesLoadedRef.current = true;
          // Default select the default workspace if not set
          setSelectedWorkspace((prev) => {
            if (!prev && wsList.length > 0) {
              const defaultWs = wsList.find((w: WorkspaceItem) => w.is_default) || wsList[0];
              setActiveWorkspaceSlug(defaultWs.slug);
              return defaultWs;
            }
            return prev;
          });
        }
      } catch (err) {
        console.error("Failed to load workspaces:", err);
      } finally {
        setLoadingWorkspaces(false);
        inFlightWorkspacesPromise.current = null;
      }
    })();
    inFlightWorkspacesPromise.current = promise;
    return promise;
  }, [slug]);

  const loadWorkspaceDetail = useCallback(async (wsSlug: string, force?: boolean) => {
    if (!slug || !wsSlug) return;
    if (!force) {
      if (loadedWsSlugRef.current === wsSlug) return;
      const inFlight = inFlightWsDetailPromise.current[wsSlug];
      if (inFlight) {
        return inFlight;
      }
    }
    const promise = (async () => {
      try {
        setLoadingWorkspaceDetail(true);
        const { data } = await api.getWorkspaceDetailApiV1B2bSlugWorkspacesWsSlugGet({
          path: { slug, ws_slug: wsSlug },
        });
        if (data) {
          setDetailedWorkspace(data as unknown as WorkspaceDetail);
          loadedWsSlugRef.current = wsSlug;
        }
      } catch (err) {
        console.error("Failed to load workspace details:", err);
      } finally {
        setLoadingWorkspaceDetail(false);
        delete inFlightWsDetailPromise.current[wsSlug];
      }
    })();
    inFlightWsDetailPromise.current[wsSlug] = promise;
    return promise;
  }, [slug]);

  const loadMembers = useCallback(async (force?: boolean) => {
    if (!slug) return;
    if (!force) {
      if (membersLoadedRef.current) return;
      if (inFlightMembersPromise.current) return inFlightMembersPromise.current;
    }
    const promise = (async () => {
      try {
        setLoadingMembers(true);
        const { data } = await api.listOrgMembersApiV1B2bSlugMembersGet({
          path: { slug },
        });
        if (data) {
          setMembers((data as any).members || []);
          membersLoadedRef.current = true;
        }
      } catch (err) {
        console.error("Failed to load members:", err);
      } finally {
        setLoadingMembers(false);
        inFlightMembersPromise.current = null;
      }
    })();
    inFlightMembersPromise.current = promise;
    return promise;
  }, [slug]);

  const loadInvites = useCallback(async (force?: boolean) => {
    if (!slug) return;
    if (!force) {
      if (invitesLoadedRef.current) return;
      if (inFlightInvitesPromise.current) return inFlightInvitesPromise.current;
    }
    const promise = (async () => {
      try {
        setLoadingInvites(true);
        const { data } = await api.listInvitationsApiV1B2bSlugInvitesGet({
          path: { slug },
        });
        if (data) {
          setInvitations((data as any).invitations || []);
          invitesLoadedRef.current = true;
        }
      } catch (err) {
        console.error("Failed to load invitations:", err);
      } finally {
        setLoadingInvites(false);
        inFlightInvitesPromise.current = null;
      }
    })();
    inFlightInvitesPromise.current = promise;
    return promise;
  }, [slug]);

  const loadDocuments = useCallback(async (force?: boolean) => {
    if (!slug) return;
    if (!force) {
      if (docsLoadedRef.current) return;
      if (inFlightDocsPromise.current) return inFlightDocsPromise.current;
    }
    const promise = (async () => {
      try {
        setLoadingDocs(true);
        const { data } = await api.listOrgDocumentsApiV1B2bSlugDocumentsGet({
          path: { slug },
        });
        if (data) {
          setDocuments((data as any).documents || []);
          docsLoadedRef.current = true;
        }
      } catch (err) {
        console.error("Failed to load documents:", err);
      } finally {
        setLoadingDocs(false);
        inFlightDocsPromise.current = null;
      }
    })();
    inFlightDocsPromise.current = promise;
    return promise;
  }, [slug]);

  const loadCredits = useCallback(async (force?: boolean) => {
    if (!slug) return;
    if (!force) {
      if (creditsLoadedRef.current) return;
      if (inFlightCreditsPromise.current) return inFlightCreditsPromise.current;
    }
    const promise = (async () => {
      try {
        setLoadingCredits(true);
        const { data } = await api.getOrgCreditsSummaryEndpointApiV1B2bSlugCreditsGet({
          path: { slug },
        });
        if (data) {
          setCreditsSummary(data);
          creditsLoadedRef.current = true;
        }
      } catch (err) {
        console.error("Failed to load credits summary:", err);
      } finally {
        setLoadingCredits(false);
        inFlightCreditsPromise.current = null;
      }
    })();
    inFlightCreditsPromise.current = promise;
    return promise;
  }, [slug]);

  // Initial load when slug changes: preload core dashboard state in parallel so counts/tabs are immediately accurate
  useEffect(() => {
    if (slug) {
      void loadStats();
      void loadCredits();
      void loadWorkspaces();
      void loadMembers();
      void loadInvites();
      void loadDocuments();
    }
  }, [slug, loadStats, loadCredits, loadWorkspaces, loadMembers, loadInvites, loadDocuments]);

  // Global refresh that re-fetches all org data and invalidates TanStack queries
  const refreshAll = useCallback(
    async (force = true) => {
      await Promise.all([
        loadStats(force),
        loadWorkspaces(force),
        loadMembers(force),
        loadInvites(force),
        loadDocuments(force),
        loadCredits(force),
      ]);
      void queryClient.invalidateQueries({ queryKey: ["b2b-learner-portal"] });
      void queryClient.invalidateQueries({ queryKey: ["b2b-admin-orgs"] });
      void queryClient.invalidateQueries({ queryKey: ["published-courses"] });
    },
    [loadStats, loadWorkspaces, loadMembers, loadInvites, loadDocuments, loadCredits, queryClient]
  );

  const isOrgAdmin = useMemo(() => {
    if (!user) return true; // fallback
    const userEmail = user.primaryEmailAddress?.emailAddress?.toLowerCase();
    const currentMember = members.find(
      (m) =>
        (m.clerk_user_id && m.clerk_user_id === user.id) ||
        (m.email && m.email.toLowerCase() === userEmail)
    );
    if (!currentMember) return true; // If they access dashboard, they are authorized
    return currentMember.role === "admin" || currentMember.role === "manager";
  }, [user, members]);

  const seatUtilization = useMemo(() => {
    const used = stats?.total_members || members.length || 0;
    const total = stats?.seat_limit || 0;
    const percentage = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
    return {
      used,
      total,
      percentage,
      isNearLimit: percentage >= 85 && percentage < 100,
      isAtLimit: total > 0 && used >= total,
    };
  }, [stats?.total_members, stats?.seat_limit, members.length]);

  const pendingInvitesCount = useMemo(() => {
    return invitations.filter((inv) => inv.status === "pending").length;
  }, [invitations]);

  const value = useMemo<OrgDashboardContextValue>(
    () => ({
      slug,
      stats,
      loadingStats,
      loadStats,
      isOrgAdmin,
      activeWorkspaceSlug,
      setActiveWorkspaceSlug,
      workspaces,
      loadingWorkspaces,
      loadWorkspaces,
      selectedWorkspace,
      setSelectedWorkspace,
      detailedWorkspace,
      loadingWorkspaceDetail,
      loadWorkspaceDetail,
      members,
      loadingMembers,
      loadMembers,
      invitations,
      loadingInvites,
      loadInvites,
      documents,
      loadingDocs,
      loadDocuments,
      creditsSummary,
      loadingCredits,
      loadCredits,
      refreshAll,
      seatUtilization,
      pendingInvitesCount,
    }),
    [
      slug,
      stats,
      loadingStats,
      loadStats,
      isOrgAdmin,
      activeWorkspaceSlug,
      workspaces,
      loadingWorkspaces,
      loadWorkspaces,
      selectedWorkspace,
      detailedWorkspace,
      loadingWorkspaceDetail,
      loadWorkspaceDetail,
      members,
      loadingMembers,
      loadMembers,
      invitations,
      loadingInvites,
      loadInvites,
      documents,
      loadingDocs,
      loadDocuments,
      creditsSummary,
      loadingCredits,
      loadCredits,
      refreshAll,
      seatUtilization,
      pendingInvitesCount,
    ]
  );

  return <OrgDashboardContext.Provider value={value}>{children}</OrgDashboardContext.Provider>;
};

export const useOrgDashboard = (): OrgDashboardContextValue => {
  const context = useContext(OrgDashboardContext);
  if (!context) {
    throw new Error("useOrgDashboard must be used within an OrgDashboardProvider");
  }
  return context;
};
